import { useTranslate } from '@refinedev/core';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Card } from 'antd';
import { useState, type ReactNode } from 'react';

/**
 * A card of a tool section (design §1.2). `role` becomes `data-role` — set ONLY here, never on an inner card — so the acceptance check can
 * read the section's card order: every 'result' (the client's data) precedes every 'config' (the therapist's own set-up). The head is a
 * wrapping flex (title + the card's own buttons) instead of antd's `extra`, which does not wrap and overflows at 390 px.
 */
export type ResultCardProps = {
  testId: string;
  title: ReactNode;
  role: 'result' | 'config';
  /** The card's own buttons (each opens a side drawer). */
  actions?: ReactNode;
  /** e.g. "Show all (11)". */
  footer?: ReactNode;
  /**
   * V-1 (2026-10-24): the card folds. Open on every load; the state is pure UI state (not stored anywhere, reset on reload). The body
   * stays mounted while folded (display:none) so a form or a scroll inside it is not lost. Off by default: the other sections look as before.
   */
  collapsible?: boolean;
  /** One line shown in the head ONLY while folded (computed from data the card already has). */
  summary?: ReactNode;
  children: ReactNode;
};

export const ResultCard = ({
  testId,
  title,
  role,
  actions,
  footer,
  collapsible,
  summary,
  children,
}: ResultCardProps) => {
  const t = useTranslate();
  const [open, setOpen] = useState(true);
  const folded = Boolean(collapsible) && !open;
  return (
  <Card
    size="small"
    data-testid={testId}
    data-role={role}
    data-open={collapsible ? String(open) : undefined}
    title={
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 0',
          whiteSpace: 'normal',
        }}
      >
        <span style={{ minWidth: 0 }}>
          {collapsible ? (
            <Button
              type="text"
              size="small"
              aria-expanded={open}
              aria-label={t(
                open ? 'pages.toolSection.cardCollapse' : 'pages.toolSection.cardExpand',
              )}
              icon={open ? <DownOutlined /> : <RightOutlined />}
              onClick={() => setOpen(!open)}
              data-testid={`${testId}-toggle`}
              style={{ marginRight: 4 }}
            />
          ) : null}
          {title}
          {folded && summary ? (
            <span
              style={{ marginLeft: 12, fontWeight: 'normal', opacity: 0.65 }}
              data-testid={`${testId}-summary`}
            >
              {summary}
            </span>
          ) : null}
        </span>
        {actions ? (
          <span
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center',
              fontWeight: 'normal',
            }}
          >
            {actions}
          </span>
        ) : null}
      </div>
    }
  >
    {collapsible ? (
      <div style={folded ? { display: 'none' } : undefined} data-testid={`${testId}-body`}>
        {children}
        {footer ? <div style={{ marginTop: 8 }}>{footer}</div> : null}
      </div>
    ) : (
      <>
        {children}
        {footer ? <div style={{ marginTop: 8 }}>{footer}</div> : null}
      </>
    )}
  </Card>
  );
};
